import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { publicRequest, userRequest } from "../utils/utils";
import {
  IAdminUserDetails,
  IAdminUsersList,
  IDeclarantsWithApplicationsResponse,
  IManagerEmployeeResponse,
  IManagersList,
  IManagerWithBrokers,
  IUpdateUserParams,
  IUser,
  IUserListResponse,
  IUserListResponseResults,
  IUserWithoutEmail,
} from "../types";
import { RootState } from "./store";

interface IUserState {
  user: IUser | null;
  usersList: IUserListResponse | null;
  managerUsersList: IManagerEmployeeResponse | null;
  declarantsList: IDeclarantsWithApplicationsResponse[] | [];
  adminUsersList: IAdminUsersList | null;
  userDetails: IUserListResponseResults | null;
  managerEmployeeDetails: IUserListResponseResults | null;
  usersListCurrentPage: number;
  managerEmployeesListCurrentPage: number;
  managersList: IManagersList[] | null;
  managerEmployeesList: IManagerEmployeeResponse | null;
  managerWithBrokers: IManagerWithBrokers | null;
  adminUserDetails: IAdminUserDetails | null;
}

const initialState: IUserState = {
  user: null,
  usersList: null,
  managerUsersList: null,
  declarantsList: [],
  adminUsersList: null,
  userDetails: null,
  managerEmployeeDetails: null,
  usersListCurrentPage: 1,
  managerEmployeesListCurrentPage: 1,
  managersList: null,
  managerEmployeesList: null,
  managerWithBrokers: null,
  adminUserDetails: null,
};

export const getMe = createAsyncThunk<
  IUser,
  void,
  { rejectValue: { error: string } }
>("user/getMe", async (_, { getState, rejectWithValue }) => {
  try {
    const access = (getState() as RootState).auth.access;

    const { data } = await userRequest.get(`users/api/profile/details/me/`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${access}`,
      },
    });
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || error.messages.message,
    });
  }
});

export const getUserDetails = createAsyncThunk<
  IUserListResponseResults,
  number,
  { rejectValue: { error: string } }
>("user/getUserDetails", async (id, { getState, rejectWithValue }) => {
  try {
    const access = (getState() as RootState).auth.access;

    const { data } = await userRequest.get(
      `/dashboard/api/manager/applications/${id}/`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${access}`,
        },
      }
    );
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || error.messages.message,
    });
  }
});

export const getAdminUserDetails = createAsyncThunk<
  IAdminUserDetails,
  number,
  { rejectValue: { error: string } }
>("user/getAdminUserDetails", async (id, { getState, rejectWithValue }) => {
  try {
    const access = (getState() as RootState).auth.access;

    const { data } = await userRequest.get(`/v5/admin/users/${id}/`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${access}`,
      },
    });
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || error.messages.message,
    });
  }
});

export const getManagerEmployeeDetails = createAsyncThunk<
  IUserListResponseResults,
  number,
  { rejectValue: { error: string } }
>(
  "user/getManagerEmployeeDetails",
  async (id, { getState, rejectWithValue }) => {
    try {
      const access = (getState() as RootState).auth.access;
      const { data } = await userRequest.get(`/users/regional/manager/${id}/`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${access}`,
        },
      });
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || error.messages.message,
      });
    }
  }
);

export const updateUser = createAsyncThunk<
  IUser,
  IUserWithoutEmail,
  { rejectValue: { error: string } }
>("user/updateUser", async (params, { getState, rejectWithValue }) => {
  try {
    const access = (getState() as RootState).auth.access;
    const { data } = await userRequest.patch(
      `/users/api/profile/details/update_profile/`,
      params,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${access}`,
        },
      }
    );
    return data;
  } catch (error) {
    console.log(error);
  }
});

export const authorizeUserByAdmin = createAsyncThunk<
  IUser,
  { id: number; params: { is_active: boolean } },
  { rejectValue: { error: string } }
>(
  "user/authorizeUserByAdmin",
  async ({ id, params }, { getState, rejectWithValue }) => {
    try {
      const access = (getState() as RootState).auth.access;
      const { data } = await userRequest.patch(
        `/v5/admin/users/${id}/`,
        params,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${access}`,
          },
        }
      );
      return data;
    } catch (error) {
      console.log(error);
    }
  }
);

export const updateMangerUser = createAsyncThunk<
  IUserListResponseResults,
  { id: number; params: IUserListResponseResults },
  { rejectValue: { error: string } }
>(
  "user/updateManagerUser",
  async ({ id, params }, { getState, rejectWithValue }) => {
    try {
      const access = (getState() as RootState).auth.access;
      const { data } = await userRequest.patch(
        `/dashboard/update-user-by-manager/${id}/`,
        params,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${access}`,
          },
        }
      );
      return data;
    } catch (error) {
      console.log(error);
    }
  }
);

export const updateUserByManager = createAsyncThunk<
  IUser,
  { id: number; params: { is_active: boolean } },
  { rejectValue: { error: string } }
>(
  "user/updateUserByManager",
  async ({ id, params }, { getState, rejectWithValue }) => {
    try {
      const access = (getState() as RootState).auth.access;
      const { data } = await userRequest.patch(
        `/users/regional/manager/${id}/`,
        params,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${access}`,
          },
        }
      );
      return data;
    } catch (error) {
      console.log(error);
    }
  }
);

export const getUsersList = createAsyncThunk<
  IAdminUsersList,
  number,
  { rejectValue: { error: string } }
>("admin/getUsersList", async (id, { getState, rejectWithValue }) => {
  try {
    const access = (getState() as RootState).auth.access;

    const { data } = await userRequest.get(`/users/getlist/?page=${id}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${access}`,
      },
    });
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || error.messages.message,
    });
  }
});

export const getManagerEmployeesList = createAsyncThunk<
  IManagerEmployeeResponse,
  number,
  { rejectValue: { error: string } }
>(
  "users/getManagerEmployeesList",
  async (id, { getState, rejectWithValue }) => {
    try {
      const { data } = await userRequest.get(
        `/users/regional/manager/?page=${id}`
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  }
);

export const getManagerWithBrokers = createAsyncThunk<
  IManagerWithBrokers,
  number,
  { rejectValue: { error: string } }
>("users/getManagerWithBrokers", async (id, { getState, rejectWithValue }) => {
  try {
    const { data } = await userRequest.get(
      `/v5/admin/users/managers_with_brokers/?page=${id}`
    );
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getManagersList = createAsyncThunk<
  IManagersList[],
  void,
  { rejectValue: { error: string } }
>("users/getManagersList", async (_, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(`/users/manager-list/`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getAdminUsersList = createAsyncThunk<
  IAdminUsersList,
  number,
  { rejectValue: { error: string } }
>("users/getAdminUsersList", async (id, { getState, rejectWithValue }) => {
  try {
    const access = (getState() as RootState).auth.access;
    const { data } = await userRequest.get(`/users/getlist/?page=${id}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${access}`,
      },
    });
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getDeclarantsWithApplications = createAsyncThunk<
  IDeclarantsWithApplicationsResponse[],
  void,
  { rejectValue: { error: string } }
>(
  "admin/getDeclarantsWithApplications",
  async (_, { getState, rejectWithValue }) => {
    try {
      const access = (getState() as RootState).auth.access;

      const { data } = await userRequest.get(`/manage/api/queue/`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${access}`,
        },
      });
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || error.messages.message,
      });
    }
  }
);

export const changeApplicationsQueue = createAsyncThunk<
  { message: string },
  {
    from_declarant: number;
    to_declarant: number;
    applications_id: number;
  },
  { rejectValue: { error: string } }
>(
  "admin/changeApplicationsQueue",
  async (params, { getState, rejectWithValue }) => {
    try {
      const access = (getState() as RootState).auth.access;

      const { data } = await userRequest.patch(
        `/manage/api/queue/update/`,
        params,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${access}`,
          },
        }
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || error.messages.message,
      });
    }
  }
);

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUsersCurrentPage: (state, action) => {
      state.usersListCurrentPage = action.payload;
    },
    setManagerEmployeesCurrentPage: (state, action) => {
      state.managerEmployeesListCurrentPage = action.payload;
    },
    resetUser: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMe.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addCase(getUserDetails.fulfilled, (state, action) => {
        state.userDetails = action.payload;
      })
      .addCase(getAdminUserDetails.fulfilled, (state, action) => {
        state.adminUserDetails = action.payload;
      })
      .addCase(getManagerEmployeeDetails.fulfilled, (state, action) => {
        state.managerEmployeeDetails = action.payload;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.user = action.payload;
      })

      .addCase(updateUserByManager.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addCase(updateMangerUser.fulfilled, (state, action) => {
        state.managerEmployeeDetails = action.payload;
      })
      .addCase(getUsersList.fulfilled, (state, action) => {
        state.adminUsersList = action.payload;
      })
      .addCase(getManagerEmployeesList.fulfilled, (state, action) => {
        state.managerUsersList = action.payload;
        state.managerEmployeesList = action.payload;
      })

      .addCase(getAdminUsersList.fulfilled, (state, action) => {
        state.adminUsersList = action.payload;
      })
      .addCase(getDeclarantsWithApplications.fulfilled, (state, action) => {
        state.declarantsList = action.payload;
      })
      .addCase(getManagersList.fulfilled, (state, action) => {
        state.managersList = action.payload;
      })
      .addCase(getManagerWithBrokers.fulfilled, (state, action) => {
        state.managerWithBrokers = action.payload;
      });
  },
});

export const {
  setUsersCurrentPage,
  setManagerEmployeesCurrentPage,
  resetUser,
} = userSlice.actions;
export default userSlice.reducer;
